/**
 * @packageDocumentation
 * Implements the animated loading screen shown before gameplay, including progress display
 * and the transition into the main app.
 *
 * Portrait layout is driven by Figma LTWH coordinates (1080x1920) using the same
 * “designRoot + placeFromFigmaLTWH” pattern as app.ts.
 */

import * as PIXI from "pixi.js";
import { Assets } from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v8";

import { loadGameAssets, loadPreloadAssets } from "./LoadAssets";
import { InstructionScreen } from "./instruction";
import { SoundPrompt } from "./Game/soundPrompt";
import { SoundManager } from "./Game/sounds";

/**
 * Preloader class that handles the loading screen and assets preloading
 * @extends PIXI.Container
 */
export class Preloader extends PIXI.Container {
  /** The game logo sprite */
  private logo!: PIXI.Sprite;
  /** The loading animation spine object */
  private preload_anim!: Spine;
  /** The loading progress text */
  private loadingText!: PIXI.Text;

  private soundPrompt?: SoundPrompt;
  private instructionScreen?: InstructionScreen;
  private soundConsent: boolean | null = null;

  // Root container representing the Figma frame (single source of truth)
  private designRoot = new PIXI.Container();

  // Resize handler bookkeeping
  private raf = 0;
  private onResizeBound = () => this.requestLayout();

  // ===== FIGMA SOURCE OF TRUTH (Portrait) =====
  private readonly FIGMA_P = {
    BASE_W: 1080,
    BASE_H: 1920,
    ROOT_OFFSET_X: 0,
    ROOT_OFFSET_Y: 0,

    // /* logo_veiledVillains */
    // width: 818, height: 529, left: 125, top: 431
    LOGO: { left: 125, top: 431, w: 818, h: 529 },

    // /* loading%_bomb */
    // width: 492, height: 560, left: 241, top: 901
    LOADING_ANIM: { left: 241, top: 901, w: 492, h: 560 },

    // /* 99% */
    // width: 117, height: 82, left: 605, top: 1293
    LOADING_TEXT: { left: 605, top: 1293, w: 117, h: 82 },
  } as const;

  /**
   * Creates an instance of Preloader
   * @param app - PIXI Application instance
   * @param app_width - legacy width (kept for compatibility)
   * @param app_height - legacy height (kept for compatibility)
   * @param playClickFun - Callback function when loading completes
   */
  constructor(
    private app: PIXI.Application,
    private app_width: number,
    private app_height: number,
    private playClickFun: () => void
  ) {
    super();
    this.sortableChildren = true;

    // designRoot mirrors app.ts: all preloader UI goes inside it.
    this.designRoot.sortableChildren = true;
    this.addChild(this.designRoot);

    (async () => {
      await loadPreloadAssets();
      this.createLogo();
      this.createPreloadAnim();
      this.createLoadingText();

      // Initial layout and wire resize listeners
      this.applyResponsiveLayout();
      this.addLayoutListeners();

      this.progressLoading();
    })();
  }

  // ---------------------------------------------------------------------------
  // Layout helpers (mirrors app.ts pattern)
  // ---------------------------------------------------------------------------

  private getScreenSize() {
    const screenW = this.app?.renderer?.screen?.width ?? this.app_width;
    const screenH = this.app?.renderer?.screen?.height ?? this.app_height;
    return { screenW, screenH };
  }

  private layoutDesignRoot(
    screenW: number,
    screenH: number,
    cfg: { BASE_W: number; BASE_H: number; ROOT_OFFSET_X: number; ROOT_OFFSET_Y: number }
  ) {
    const s = Math.min(screenW / cfg.BASE_W, screenH / cfg.BASE_H);

    this.designRoot.scale.set(s);
    this.designRoot.x = (screenW - cfg.BASE_W * s) / 2 + cfg.ROOT_OFFSET_X;
    this.designRoot.y = (screenH - cfg.BASE_H * s) / 2 + cfg.ROOT_OFFSET_Y;

    return s;
  }

  private figmaCenter(left: number, top: number, w: number, h: number) {
    return { x: left + w / 2, y: top + h / 2 };
  }

  /**
   * Places ANY display object by Figma left/top/width/height.
   * Uses local bounds to pivot correctly (works for Containers too).
   */
  private placeFromFigmaLTWH(
    obj: PIXI.Container,
    left: number,
    top: number,
    w: number,
    h: number,
    opts?: { fitToSize?: boolean; nudgeX?: number; nudgeY?: number }
  ) {
    const { fitToSize = false, nudgeX = 0, nudgeY = 0 } = opts ?? {};

    // Optional proportional scale to match Figma size.
    if (fitToSize && (obj as any).width != null && (obj as any).height != null) {
      const anyObj = obj as any as { width: number; height: number; scale: PIXI.ObservablePoint };
      const sx = w / anyObj.width;
      const sy = h / anyObj.height;
      anyObj.scale.set(anyObj.scale.x * sx, anyObj.scale.y * sy);
    }

    // Pivot to true visual center.
    const b = obj.getLocalBounds();
    obj.pivot.set(b.x + b.width / 2, b.y + b.height / 2);

    const c = this.figmaCenter(left, top, w, h);
    obj.position.set(c.x + nudgeX, c.y + nudgeY);
  }

  /**
   * Scales + centers a container so its visual bounds fit inside a target LTWH region.
   * Useful for Spine since its pixel size depends on export + runtime bounds.
   */
  private fitContainerToFigmaLTWH(
    container: PIXI.Container,
    target: { left: number; top: number; w: number; h: number },
    opts?: { cover?: boolean; nudgeX?: number; nudgeY?: number }
  ) {
    const { cover = false, nudgeX = 0, nudgeY = 0 } = opts ?? {};
    const b = container.getLocalBounds();
    if (b.width < 1 || b.height < 1) return false;

    const sx = target.w / b.width;
    const sy = target.h / b.height;
    const s = cover ? Math.max(sx, sy) : Math.min(sx, sy);

    container.scale.set(s);
    container.pivot.set(b.x + b.width / 2, b.y + b.height / 2);
    container.position.set(target.left + target.w / 2 + nudgeX, target.top + target.h / 2 + nudgeY);
    return true;
  }

  private requestLayout() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(() => this.applyResponsiveLayout());
  }

  private addLayoutListeners() {
    window.addEventListener("resize", this.onResizeBound);
    window.addEventListener("orientationchange", this.onResizeBound);
    window.visualViewport?.addEventListener("resize", this.onResizeBound);
    window.visualViewport?.addEventListener("scroll", this.onResizeBound);
  }

  private removeLayoutListeners() {
    window.removeEventListener("resize", this.onResizeBound);
    window.removeEventListener("orientationchange", this.onResizeBound);
    window.visualViewport?.removeEventListener("resize", this.onResizeBound);
    window.visualViewport?.removeEventListener("scroll", this.onResizeBound);
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  private applyResponsiveLayout() {
    const { screenW, screenH } = this.getScreenSize();
    const portrait = screenH > screenW;

    if (portrait) this.applyPortraitLayout(screenW, screenH);
    else this.applyLandscapeLayout(screenW, screenH);
  }

  // ---------------------------------------------------------------------------
  // Portrait / Landscape layouts
  // ---------------------------------------------------------------------------

  /**
   * Portrait layout: exact LTWH mapping to the 1080x1920 Figma frame.
   */
  private applyPortraitLayout(screenW: number, screenH: number) {
    // Scale/center the Figma design space into the screen.
    this.layoutDesignRoot(screenW, screenH, this.FIGMA_P);

    // Reset orientation residue
    this.logo.rotation = 0;
    this.preload_anim.rotation = 0;
    this.loadingText.rotation = 0;

    // Ensure children are in designRoot
    if (this.logo.parent !== this.designRoot) this.designRoot.addChild(this.logo);
    if (this.preload_anim.parent !== this.designRoot) this.designRoot.addChild(this.preload_anim);
    if (this.loadingText.parent !== this.designRoot) this.designRoot.addChild(this.loadingText);

    // Logo: match Figma size + placement
    this.logo.scale.set(1);
    this.placeFromFigmaLTWH(
      this.logo as any,
      this.FIGMA_P.LOGO.left,
      this.FIGMA_P.LOGO.top,
      this.FIGMA_P.LOGO.w,
      this.FIGMA_P.LOGO.h,
      { fitToSize: true }
    );

    // Spine: fit to the target region (keeps crispness; avoids arbitrary 4-5x scaling)
    this.preload_anim.scale.set(1);
    const fitted = this.fitContainerToFigmaLTWH(this.preload_anim as any, this.FIGMA_P.LOADING_ANIM, {
      cover: false,
    });

    // If bounds aren't ready on first frame, try again next frame.
    if (!fitted) requestAnimationFrame(() => this.requestLayout());

    // Percent text: exact placement; scale to fit into the target box
    this.loadingText.scale.set(1);
    this.placeFromFigmaLTWH(
      this.loadingText as any,
      this.FIGMA_P.LOADING_TEXT.left,
      this.FIGMA_P.LOADING_TEXT.top,
      this.FIGMA_P.LOADING_TEXT.w,
      this.FIGMA_P.LOADING_TEXT.h,
      { fitToSize: true }
    );
  }

  /**
   * Landscape layout: keep existing behavior (centered composition).
   * If you later provide landscape Figma LTWH, we can convert this to Figma-driven as well.
   */
  private applyLandscapeLayout(screenW: number, screenH: number) {
    // In landscape, treat the stage itself as the layout space (no portrait Figma frame).
    this.designRoot.scale.set(1);
    this.designRoot.position.set(0, 0);

    if (this.logo.parent !== this.designRoot) this.designRoot.addChild(this.logo);
    if (this.preload_anim.parent !== this.designRoot) this.designRoot.addChild(this.preload_anim);
    if (this.loadingText.parent !== this.designRoot) this.designRoot.addChild(this.loadingText);

    this.logo.anchor.set(0.5);
    this.logo.scale.set(1.8);
    this.logo.position.set(screenW / 2, screenH / 2 - 120);

    this.preload_anim.scale.set(1);
    this.preload_anim.position.set(screenW / 2, screenH / 2 + 440);

    this.loadingText.scale.set(1);
    this.loadingText.position.set(screenW / 2 + 60, screenH / 2 + 305);
  }

  // ---------------------------------------------------------------------------
  // Element creation
  // ---------------------------------------------------------------------------

  /** Creates and adds the game logo */
  private createLogo() {
    this.logo = new PIXI.Sprite(Assets.get("logo"));
    this.logo.anchor.set(0.5);
    this.designRoot.addChild(this.logo);
  }

  /** Creates and adds the loading animation (Spine) */
  private createPreloadAnim() {
    this.preload_anim = Spine.from({
      skeleton: "preloader_anim_json",
      atlas: "preloader_anim_atlas",
      scale: 1,
    });

    this.preload_anim.state.timeScale = 0.4;
    this.preload_anim.state.setAnimation(0, "bomb_idle", true);
    this.preload_anim.pivot.set(0);

    this.designRoot.addChild(this.preload_anim);
  }

  /** Creates and adds the loading progress text */
  private createLoadingText() {
    this.loadingText = new PIXI.Text({
      text: "0%",
      style: new PIXI.TextStyle({
        fill: "#fff",
        stroke: {
          color: "#000",
          width: 5,
        },
        fontSize: 36,
        fontFamily: "PermanentMarker",
      }),
    });

    this.loadingText.anchor.set(0.5);
    this.designRoot.addChild(this.loadingText);
  }

  // ---------------------------------------------------------------------------
  // Loading flow
  // ---------------------------------------------------------------------------

  /** Handles the loading progress and updates the UI */
  private async progressLoading() {
    await loadGameAssets((progress: number) => {
      const percentage = Math.floor(progress * 100);
      this.loadingText.text = `${percentage}%`;

      // Text size can change as digits change; keep it pinned to the Figma box.
      // (Portrait uses fitToSize.)
      this.requestLayout();
    });

    setTimeout(() => {
      // Clean up layout listeners once preloader content is no longer visible.
      this.removeLayoutListeners();

      this.designRoot.removeChild(this.loadingText);
      this.designRoot.removeChild(this.preload_anim);
      this.designRoot.removeChild(this.logo);

      this.promptSoundPreference();
    }, 1000);
  }

  private promptSoundPreference() {
    if (this.soundPrompt) {
      this.removeChild(this.soundPrompt);
      this.soundPrompt.destroy({ children: true });
      this.soundPrompt = undefined;
    }

    const { screenW, screenH } = this.getScreenSize();
    const prompt = new SoundPrompt({
      width: screenW,
      height: screenH,
      onChoice: (enabled: boolean) => this.onSoundPreferenceSelected(enabled),
    });

    this.soundPrompt = prompt;
    this.addChild(prompt);
  }

  private onSoundPreferenceSelected(enabled: boolean) {
    this.soundConsent = enabled;
    if (this.soundPrompt) {
      this.removeChild(this.soundPrompt);
      this.soundPrompt.destroy({ children: true });
      this.soundPrompt = undefined;
    }
    this.showInstructionScreen();
  }

  private showInstructionScreen() {
    if (this.instructionScreen) return;

    if (this.getSoundConsent()) SoundManager.bgMusicSndPlay();
    else SoundManager.MuteBgMusicFun();

    const instructionScreen = new InstructionScreen(this.app, this.playClickFun);
    instructionScreen.zIndex = 10;
    this.instructionScreen = instructionScreen;
    this.addChild(instructionScreen);
  }

  getSoundConsent() {
    return this.soundConsent === true;
  }

  override destroy(options?: boolean | PIXI.DestroyOptions) {
    this.removeLayoutListeners();
    super.destroy(options as any);
  }
}
